import { motion } from 'framer-motion'
import {
  HiChartBar,
  HiCloud,
  HiInformationCircle,
  HiLockClosed,
  HiShieldCheck,
  HiUserGroup,
} from 'react-icons/hi'
import { SectionTitle } from '@/components/SectionTitle'
import { useLanguage } from '@/i18n/LanguageContext'

const corporateLogos = [
  { file: 'fcamara.png', alt: 'FCamara' },
  { file: 'careplus.png', alt: 'CarePlus' },
  { file: 'artit.png', alt: 'artit' },
  { file: 'tcs.png', alt: 'TCS' },
  { file: 'infosys.png', alt: 'Infosys' },
  { file: 'brq.png', alt: 'BRQ' },
  { file: 'itau.png', alt: 'Itaú' },
  { file: 'citibank.png', alt: 'Citibank' },
  { file: 'aon.png', alt: 'AON' },
  { file: 'vale.png', alt: 'Vale' },
  { file: 'demarest.png', alt: 'Demarest' },
  { file: 'braspress.png', alt: 'Braspress' },
  { file: 'bradesco.png', alt: 'Bradesco' },
  { file: 'hsbc.png', alt: 'HSBC' },
  { file: 'standard-bank.png', alt: 'Standard Bank' },
  { file: 'deloitte.png', alt: 'Deloitte' },
  { file: 'atlantico.png', alt: 'Atlântico' },
  { file: 'santander.png', alt: 'Santander' },
  { file: 'banco-abc.svg', alt: 'Banco ABC Brasil' },
  { file: 'votorantim.png', alt: 'Votorantim' },
  { file: 'rabobank.png', alt: 'Rabobank' },
  { file: 'sompo.png', alt: 'Sompo Seguros' },
  { file: 'bpi.png', alt: 'BPI' },
  { file: 'irb-brasil.png', alt: 'IRB Brasil RE' },
  { file: 'multiplan.png', alt: 'Multiplan' },
  { file: 'qualicorp.png', alt: 'Qualicorp' },
] as const

const pillarIcons = [
  HiShieldCheck,
  HiUserGroup,
  HiChartBar,
  HiLockClosed,
  HiCloud,
] as const

export function ExperienciaCorporativa() {
  const { t } = useLanguage()

  return (
    <section id="experiencia-corporativa" className="section-padding bg-white">
      <div className="container-custom">
        <SectionTitle
          title={t.experienciaCorporativa.title}
          subtitle={t.experienciaCorporativa.subtitle}
        />

        <motion.p
          className="max-w-4xl mx-auto -mt-6 md:-mt-8 mb-10 md:mb-12 text-center text-slate-600 leading-relaxed"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.45 }}
        >
          {t.experienciaCorporativa.description}
        </motion.p>

        <motion.div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5 }}
        >
          {corporateLogos.map((logo, index) => (
            <motion.div
              key={logo.file}
              className="flex items-center justify-center rounded-xl border border-slate-200 bg-white p-4 md:p-5 min-h-[80px] md:min-h-[92px] shadow-sm hover:border-primary-200 hover:shadow-md transition-all duration-300"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.02 }}
            >
              <img
                src={`/experiencia-corporativa/logos/${logo.file}`}
                alt={logo.alt}
                className="max-h-9 md:max-h-11 w-full max-w-[120px] object-contain object-center"
                loading="lazy"
                decoding="async"
              />
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          className="mt-8 md:mt-10 flex items-start justify-center gap-2 max-w-3xl mx-auto text-sm text-slate-500 leading-relaxed text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.1 }}
        >
          <HiInformationCircle className="h-5 w-5 shrink-0 text-primary-500 mt-0.5" aria-hidden="true" />
          <span>{t.experienciaCorporativa.disclaimer}</span>
        </motion.p>

        <motion.div
          className="mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          {t.experienciaCorporativa.pillars.map((label, index) => {
            const Icon = pillarIcons[index]
            return (
              <div
                key={label}
                className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 md:py-4"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <span className="text-sm font-semibold text-slate-700 leading-snug">{label}</span>
              </div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}
